import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { postOrQueue, isOnline, startAutoFlush } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { Bus, LogOut, ChevronRight, Sunrise, Sunset, Lock, Warehouse, LogIn, Coffee, Play, Square, Hand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DriverMessages from "@/components/DriverMessages";
import DriverPTT from "@/components/DriverPTT";
import Copyright from "@/components/Copyright";
import BrandLogo from "@/components/BrandLogo";

const STATUS = { assigned: "Non démarrée", in_progress: "En cours", completed: "Terminée" };
const LABELS = { IN: "Quart débuté", BREAK_START: "Pause débutée", BREAK_END: "Retour de pause", OUT: "Quart terminé" };
const PUNCH = {
  IN: { label: "PUNCH IN", icon: LogIn, color: "bg-emerald-500 text-slate-900" },
  BREAK_START: { label: "PAUSE", icon: Coffee, color: "bg-amber-500 text-slate-900" },
  BREAK_END: { label: "RETOUR", icon: Play, color: "bg-blue-500 text-white" },
  OUT: { label: "PUNCH OUT", icon: Square, color: "bg-red-500 text-white" },
};
const hm = (m) => `${Math.floor((m || 0) / 60)}h${String(Math.round((m || 0) % 60)).padStart(2, "0")}`;
const BLOCKS = [
  { key: "AM", label: "Bloc du matin", icon: Sunrise },
  { key: "PM", label: "Bloc du soir", icon: Sunset },
];

export default function DriverHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [clock, setClock] = useState(null);
  const [busy, setBusy] = useState(false);
  const [free, setFree] = useState([]);
  const [buses, setBuses] = useState([]);
  const [claimFor, setClaimFor] = useState(null);
  const loadFree = () => api.get("/driver/available").then((r) => setFree(r.data)).catch(() => setFree([]));
  useEffect(() => {
    api.get("/driver/routes").then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/driver/timeclock").then((r) => setClock(r.data)).catch(() => {});
    api.get("/driver/buses").then((r) => setBuses(r.data)).catch(() => {});
    loadFree();
  }, []);

  const claim = async (route_id, bus_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/claim", { route_id, bus_id });
      toast.success("Route prise — bonne route !");
      setClaimFor(null);
      nav(`/driver/${data.assignment_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      loadFree();
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const cancel = startAutoFlush((sent) => {
      toast.success(`${sent} action(s) synchronisée(s)`);
      api.get("/driver/timeclock").then((r) => setClock(r.data)).catch(() => {});
      api.get("/driver/routes").then((r) => setRoutes(r.data)).catch(() => {});
    });
    return cancel;
  }, []);

  const punch = async (action) => {
    setBusy(true);
    try {
      const at = new Date().toISOString();
      const pos = await new Promise((res) => navigator.geolocation
        ? navigator.geolocation.getCurrentPosition((p) => res(p.coords), () => res(null), { timeout: 4000 })
        : res(null));
      const r = await postOrQueue("/driver/timeclock", { action, at, lat: pos?.latitude ?? null, lng: pos?.longitude ?? null });
      if (r?.data?.queued) toast.message("Hors réseau — poinçon enregistré, envoi automatique");
      else { setClock(r.data); toast.success(LABELS[action]); }
      if (isOnline()) api.get("/driver/timeclock").then((x) => setClock(x.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const dates = [...new Set(routes.map((a) => a.date))].sort().reverse();
  const day = dates.includes(today) ? today : dates[0];
  const ofDay = routes.filter((a) => a.date === day);

  return (
    <div className="driver-app min-h-screen bg-slate-950 text-white">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-yellow-400 flex items-center justify-center overflow-hidden"><BrandLogo size={38} fallbackClassName="!bg-transparent" className="p-0.5" /></div>
          <div><div className="font-display font-extrabold text-lg leading-none">Innova Route</div><div className="text-xs text-white/50 mt-0.5">Bonjour, {user?.name}</div></div>
        </div>
        <Button variant="ghost" size="icon" className="driver-tap text-white/80" onClick={async () => { await logout(); nav("/login"); }} data-testid="driver-logout"><LogOut className="w-5 h-5" /></Button>
      </div>

      <div className="px-3 py-4 space-y-5">
        <DriverPTT />
        {clock && (
          <div className="rounded-2xl bg-slate-900 border border-white/10 p-4" data-testid="punch-card">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Poinçon</span>
              <span className="text-sm font-mono" data-testid="punch-totals">
                {hm(clock.worked_min)} travaillé · {Math.round(clock.break_min)} min pause
              </span>
            </div>
            <div className="text-sm mt-1" data-testid="punch-state">
              {clock.on_break ? "En pause" : clock.punched_in ? "Au travail" : clock.last_action === "OUT" ? "Quart terminé" : "Pas encore poinçonné"}
            </div>
            <div className={`grid gap-2 mt-3 ${clock.next_actions.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
              {clock.next_actions.map((a) => {
                const p = PUNCH[a]; const Icon = p.icon;
                return (
                  <button key={a} onClick={() => punch(a)} disabled={busy} data-testid={`punch-${a}`}
                    className={`h-16 rounded-xl ${p.color} font-display text-lg font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50`}>
                    <Icon className="w-5 h-5" /> {p.label}
                  </button>
                );
              })}
            </div>
            {clock.entries?.length > 0 && (
              <div className="text-[11px] text-white/50 mt-2 font-mono">
                {clock.entries.map((e) => `${{ IN: "Entrée", BREAK_START: "Pause", BREAK_END: "Retour", OUT: "Sortie" }[e.action]} ${e.at.slice(11, 16)}`).join(" · ")}
              </div>
            )}
          </div>
        )}
        {!ofDay.length && !free.length && <div className="text-white/50 text-center py-20" data-testid="no-routes">Aucune route assignée.</div>}
        {day && ofDay.length > 0 && <div className="overline text-white/40">Journée du {day}</div>}

        {BLOCKS.map(({ key, label, icon: Icon }) => {
          const items = ofDay.filter((a) => a.period === key);
          if (!items.length) return null;
          const locked = items.every((a) => a.blocked);
          return (
            <div key={key} data-testid={`block-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-yellow-400" />
                <span className="font-display font-bold text-lg">{label}</span>
                <span className="text-xs text-white/40">{items.length} route(s)</span>
                {locked && <Lock className="w-4 h-4 text-white/30" />}
              </div>
              <div className={`space-y-3 ${locked ? "opacity-40 pointer-events-none" : ""}`}>
                {items.map((a) => (
                  <Link key={a.id} to={`/driver/${a.id}`} data-testid={`driver-route-${a.route_number}`}
                    className="block rounded-2xl bg-slate-900 border border-white/10 p-4 active:scale-[0.98] transition-transform">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-xs text-white/40 font-mono">Route {a.block_order} de {a.block_total}</div>
                        <div className="font-display text-2xl font-extrabold mt-0.5">Route {a.route_number}</div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-white/60 text-sm">
                          <span className="flex items-center gap-1.5"><Bus className="w-4 h-4" /> Autobus {a.bus_fleet}</span>
                          {a.departure_time && <span className="font-mono">{a.departure_time}</span>}
                          {a.school && <span className="truncate">{a.school}</span>}
                        </div>
                        {a.is_last_of_block && (
                          <div className="flex items-center gap-1.5 mt-2 text-xs text-yellow-400" data-testid={`ends-garage-${a.route_number}`}>
                            <Warehouse className="w-4 h-4" /> Dernière route du bloc — retour au garage
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className={a.status === "in_progress" ? "bg-emerald-500" : a.status === "completed" ? "bg-slate-600" : "bg-blue-500"}>{STATUS[a.status]}</Badge>
                        <ChevronRight className="w-6 h-6 text-white/40 mt-3 ml-auto" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {locked && <div className="text-xs text-white/40 mt-2">Terminez le bloc du matin pour débloquer.</div>}
            </div>
          );
        })}
      </div>
      {free.length > 0 && (
        <div className="px-3 pb-8" data-testid="free-routes">
          <div className="flex items-center gap-2 mb-3">
            <Hand className="w-5 h-5 text-yellow-400" />
            <span className="font-display font-bold text-lg">Routes disponibles</span>
            <span className="text-xs text-white/40">{free.length} libre(s)</span>
          </div>
          <div className="space-y-3">
            {free.map((f) => (
              <div key={f.route_id} className="rounded-2xl bg-slate-900 border border-white/10 p-4" data-testid={`free-route-${f.route_number}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-2xl font-extrabold">Route {f.route_number}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-white/60 text-sm">
                      <Badge className={f.period === "AM" ? "bg-blue-500" : "bg-indigo-500"}>{f.period === "AM" ? "Matin" : "Soir"}</Badge>
                      {f.departure_time && <span className="font-mono">{f.departure_time}</span>}
                      <span>{f.stops} arrêts</span>
                      {f.school && <span className="truncate">{f.school}</span>}
                    </div>
                  </div>
                  <Button className="bg-yellow-400 text-slate-900 hover:bg-yellow-300 font-bold shrink-0"
                    data-testid={`take-route-${f.route_number}`}
                    onClick={() => setClaimFor(claimFor === f.route_id ? null : f.route_id)}>
                    Prendre
                  </Button>
                </div>
                {claimFor === f.route_id && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-xs text-white/50 mb-2">Choisissez votre autobus</div>
                    {!buses.length && <div className="text-sm text-white/60">Aucun autobus dans votre garage.</div>}
                    <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto">
                      {buses.map((b) => (
                        <button key={b.id} disabled={busy} onClick={() => claim(f.route_id, b.id)}
                          data-testid={`claim-bus-${b.fleet_number}`}
                          className="driver-tap rounded-xl bg-white/5 border border-white/15 px-2 py-3 font-mono text-sm font-bold active:scale-95 transition-transform disabled:opacity-50">
                          {b.fleet_number}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <DriverMessages />
      <Copyright dark />
    </div>
  );
}
